import { memo } from 'react';
import { Modal, Stack, Text, SimpleGrid } from '@mantine/core';
import type { CourseReview } from '../../services/userManager';

type CourseFeedback = {
  courseId: number;
  courseName: string;
  review?: CourseReview | string | null;
};

type CourseFeedbackModalProps = {
  opened: boolean;
  onClose: () => void;
  course: CourseFeedback | undefined;
};

const formatValue = (value: unknown): string => {
  if (value == null || value === '') return '—';
  return String(value);
};

const parseCourseReview = (
  review: CourseFeedback['review']
): CourseReview | null => {
  if (review == null) return null;
  if (typeof review === 'string') {
    try {
      return JSON.parse(review) as CourseReview;
    } catch {
      return null;
    }
  }
  if (typeof review === 'object') return review;
  return null;
};

const FIELDS: Array<{ key: keyof CourseReview; label: string }> = [
  { key: 'relevance', label: 'Relevance' },
  { key: 'quality', label: 'Quality' },
  { key: 'utility', label: 'Utility' },
  { key: 'time', label: 'Time' },
  { key: 'sponsor', label: 'Sponsor' },
  { key: 'notes', label: 'Notes' },
];

export const CourseFeedbackModal = memo<CourseFeedbackModalProps>(
  ({ opened, onClose, course }) => {
    const review = parseCourseReview(course?.review);

    return (
      <Modal
        opened={opened}
        onClose={onClose}
        title={course ? `Feedback — ${course.courseName}` : 'Feedback'}
        size='md'
      >
        {!course || !review ? (
          <Text c='dimmed' size='sm'>
            Nessun feedback presente
          </Text>
        ) : (
          <Stack gap='md'>
            <Text size='sm' c='dimmed'>
              course_id: {course.courseId}
            </Text>
            <SimpleGrid cols={2} spacing='md'>
              {FIELDS.map(({ key, label }) => (
                <Stack key={key} gap={2}>
                  <Text size='xs' c='dimmed' tt='uppercase' fw={600}>
                    {label}
                  </Text>
                  <Text size='sm' style={{ wordBreak: 'break-word' }}>
                    {formatValue(review[key])}
                  </Text>
                </Stack>
              ))}
            </SimpleGrid>
          </Stack>
        )}
      </Modal>
    );
  }
);

CourseFeedbackModal.displayName = 'CourseFeedbackModal';
